<template>
  <dt-button kind="muted" importance="clear" class="d-w100p dp3-search-box" @click="isOpen = !isOpen">
    <dt-icon name="search" size="200" class="dp3-search-box--icon" />
    <span>Search Dialpad</span>
  </dt-button>
  <dt-modal
    v-model:show="isOpen"
    :close-button-props="{ ariaLabel: `Close` }"
    :hideClose="true"
    :visuallyHiddenClose="true"
    visuallyHiddenCloseLabel="Close"
    class="d-jc-flex-start dp3-modal"
    dialogClass="d-gg0 d-p0 d-w100p d-bar8 d-wmx828 d-bs-none"
    contentClass="d-px8 d-py16"
  >
    <template #header>
      <dt-input
        v-model="query"
        type="search"
        placeholder="Search or ask Dialpad a question…"
        ref="searchInput"
        clearable
        inputWrapperClass="dp3-search-input"
        inputClass="d-baw0"
        iconSize="lg"
      >
        <template #leftIcon>
          <dt-icon-search size="300" />
        </template>
        <template v-if="query.length !== 0" #rightIcon="{ clear }">
          <dt-button kind="muted" importance="clear" size="xs" aria-label="Clear search" @click="clear">
            <template #icon>
              <dt-icon-close size="300" />
            </template>
          </dt-button>
        </template>
      </dt-input>
    </template>
    <!-- Idle State Suggestions -->
    <dt-stack v-if="!query" gap="500">
      <dt-list-item-group
        v-if="suggestions.actions.length"
        heading="Actions"
        heading-class="d-px8 d-headline--sm d-lh-500 d-fc-tertiary"
      >
        <dt-list-item v-for="(a, i) in suggestions.actions" :key="i" type="custom">
          <dt-button
            kind="muted"
            importance="clear"
            class="d-px8 d-py4 d-gg8 d-w100p d-hmn32 d-bar4 d-fw-normal dp3-fs-150"
            labelClass="d-jc-unset"
            @click="performAction(a.label)"
          >
            <template #icon>
              <dt-icon :name="a.icon" size="200" class="d-o50" />
            </template>
            {{ a.label }}
          </dt-button>
        </dt-list-item>
      </dt-list-item-group>
      <dt-list-item-group
        v-if="suggestions.recentSearches.length"
        heading="Recent searches"
        heading-class="d-px8 d-headline--sm d-lh-500 d-fc-tertiary"
      >
        <dt-list-item v-for="(r, i) in suggestions.recentSearches" :key="i" type="custom">
          <dt-button
            kind="muted"
            importance="clear"
            class="d-px8 d-py4 d-gg8 d-w100p d-hmn32 d-bar4 d-fw-normal dp3-fs-150"
            labelClass="d-jc-unset"
            @click="performAction(r)"
          >
            <template #icon>
              <dt-icon-clock-3 size="200" class="d-o50" />
            </template>
            {{ r }}
          </dt-button>
        </dt-list-item>
      </dt-list-item-group>
      <dt-list-item-group
        v-if="enrichedContacts.length"
        heading="Frequent contacts"
        heading-class="d-d-flex d-ai-center d-gc2 d-px8 d-headline--sm d-lh-500 d-fc-tertiary"
        class="d-d-grid d-g-cols-2 d-g-rows6 d-cg8"
      >
        <dt-list-item v-for="c in enrichedContacts" :key="c.id" type="custom">
          <dt-button
            kind="muted"
            importance="clear"
            class="d-px8 d-py4 d-gg8 d-w100p d-hmn32 d-bar4 d-fw-normal dp3-fs-150"
            labelClass="d-jc-unset"
            @click="performAction(c)"
          >
            <template #icon>
              <ContactAvatar v-bind="c.avatarProps" />
            </template>
            {{ c.name }}
          </dt-button>
        </dt-list-item>
      </dt-list-item-group>
    </dt-stack>

    <!-- Results -->
    <dt-stack v-else gap="500">
      <dt-list-item-group
        v-if="filteredResults.length"
        heading="Results"
        heading-class="d-px8 d-headline--sm d-lh-500 d-fc-tertiary"
      >
        <dt-list-item v-for="item in filteredResults" :key="item.id" type="custom">
          <dt-button
            kind="muted"
            importance="clear"
            class="d-px8 d-py4 d-gg8 d-w100p d-hmn32 d-bar4 d-fw-normal dp3-fs-150"
            @click="performAction(item)"
          >
            {{ item.label }}
          </dt-button>
        </dt-list-item>
      </dt-list-item-group>
      <div v-else class="d-px8 d-py4 d-fc-tertiary">No results found for “{{ query }}”</div>
    </dt-stack>
    <template #footer>
      <dt-stack gap="500" direction="row" class="d-w100p d-px12 d-py12 d-bt d-bc-subtle">
        <dt-keyboard-shortcut shortcut="{arrow-up} {arrow-down} Navigate items" class="d-baw0" />
        <dt-keyboard-shortcut shortcut="{enter} Select item" class="d-baw0" />
        <dt-keyboard-shortcut shortcut="{cmd}+C Call" class="d-baw0" />
        <dt-keyboard-shortcut shortcut="{cmd}+L Copy link" class="d-baw0" />
      </dt-stack>
    </template>
  </dt-modal>
</template>

<script setup>
import {
  DtIcon,
  DtButton,
  DtModal,
  DtInput,
  DtListItemGroup,
  DtListItem,
  DtStack,
  DtKeyboardShortcut,
} from '@dialpad/dialtone/vue3';

import { DtIconSearch, DtIconClose, DtIconClock3 } from '@dialpad/dialtone-icons/vue3';

import { ref, watch, computed, nextTick } from 'vue';
import ContactAvatar from '@/components/ContactAvatar.vue';
import { useDataStore } from '@/store/useDataStore';
import { useUserStore } from '@/store/useUserStore';
import { useAvatarUtilities } from '@/composables/ui/useAvatarUtilities';

const isOpen = ref(false);
const query = ref('');
const searchInput = ref(null);
const dataStore = useDataStore();
const userStore = useUserStore();
const currentUserId = computed(() => userStore.userId);

// Initialize avatar utilities composable
const { createAvatarPropsFromContact } = useAvatarUtilities(currentUserId.value);

const contactsById = computed(() => {
  return Object.fromEntries(dataStore.contactsData.contacts.map(c => [c.id, c]));
});

const enrichedContacts = computed(() => {
  const userList = dataStore.userContactsData[currentUserId.value] || [];
  return userList
    .map(u => {
      const contact = contactsById.value[u.contact_id];
      if (!contact) return null;

      // Create avatar props using the composable (without presence)
      const avatarProps = createAvatarPropsFromContact(contact, { size: 'xs' });

      return {
        ...contact,
        avatarProps,
      };
    })
    .filter(Boolean)
    .slice(0, 10);
});

// Idle suggestions
const suggestions = {
  actions: [
    {
      id: 'call',
      label: 'Call',
      icon: 'phone',
    },
    {
      id: 'meeting',
      label: 'Schedule a meeting',
      icon: 'calendar-plus',
    },
  ],
  recentSearches: ['"GTM report"', 'website redesign', 'spring launch plan'],
};

// Combine everything for filtering
const allItems = computed(() => [
  ...suggestions.actions.map(a => ({ id: a.id, label: a.label })),
  ...suggestions.recentSearches.map((r, i) => ({
    id: `recent-${i}`,
    label: r,
  })),
  ...enrichedContacts.value.map(c => ({ id: c.id, label: c.name })),
]);

const filteredResults = computed(() => {
  const q = query.value.toLowerCase();
  return allItems.value.filter(item => item.label.toLowerCase().includes(q));
});

// Focus the input when the popover opens and clear query when it closes
watch(isOpen, async val => {
  if (val) {
    await nextTick();
    searchInput.value?.focus();
  } else {
    query.value = '';
  }
});

function performAction(action) {
  isOpen.value = false;
}
</script>

<style lang="less">
.d-btn.dp3-search-box {
  position: relative;
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  justify-content: center;
  height: 100%;
  height: var(--dp3-top-bar-element-height);
  background-color: var(--dt-theme-topbar-field-color-background);
  border-width: 0;
  box-shadow: inset 0 0 0 var(--dt-size-100) hsla(var(--dt-theme-topbar-field-color-border-hsl) / 0.05);
  border-radius: var(--dt-size-400);
  color: var(--dt-theme-topbar-field-color-foreground);
  font-size: var(--dt-font-size-125);
  font-weight: var(--fw-normal);
}
// Position icon absolutely
.dp3-search-box--icon {
  position: absolute;
  top: calc(50% - 7px);
  left: var(--dt-space-450);
}

// Search input
.d-input__wrapper.dp3-search-input {
  // First reset the input styles
  outline: none;
  box-shadow: none;
  border-radius: var(--dt-size-0);
  border-width: var(--dt-size-0);
  background-color: transparent;

  // Then add the styles we want
  height: var(--dt-size-650);
  padding-left: var(--dt-space-300);
  padding-right: var(--dt-space-300);
  border-bottom-width: var(--dt-size-100);

  &:focus-within {
    --input-color-border: var(--dt-inputs-color-border-hover);
    box-shadow: none;
  }
}

.dp3-modal {
  --modal-dialog-padding: var(--dt-space-650);

  backdrop-filter: blur(var(--dt-size-100));

  .d-modal__content {
    max-width: unset;
  }
}
</style>
